import React, { useEffect, useRef, useState } from "react";
import JsBarcode from "jsbarcode";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Printer, Barcode as BarcodeIcon } from "lucide-react";

const SIZES = {
  small: { label: "Small (40×25 mm)", w: "40mm", h: "25mm", barcodeH: 26, fontSize: 8 },
  medium: { label: "Medium (58×32 mm)", w: "58mm", h: "32mm", barcodeH: 34, fontSize: 10 },
  large: { label: "Large (80×50 mm)", w: "80mm", h: "50mm", barcodeH: 55, fontSize: 12 },
};

/**
 * Barcode label printer.
 * - Uses product SKU as barcode value (CODE128 — works on all handheld scanners).
 * - Optionally suffixes the batch # in the human-readable line.
 * - Prints an N×M grid on the page; uses browser print dialog.
 */
export default function LabelPrintDialog({ open, onOpenChange, product }) {
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState(""); // batch_no
  const [copies, setCopies] = useState(12);
  const [size, setSize] = useState("small");
  const [showPrice, setShowPrice] = useState(true);
  const [showBatch, setShowBatch] = useState(true);
  const previewRef = useRef(null);

  useEffect(() => {
    if (!open || !product) return;
    api.get(`/products/${product.id}/batches`).then((r) => {
      setBatches(r.data || []);
      if (r.data?.length && !batch) setBatch(r.data[0].batch_no);
    }).catch(() => setBatches([]));
  }, [open, product?.id]);

  // Render preview barcode on any change
  useEffect(() => {
    if (!open || !product || !previewRef.current) return;
    const raf = requestAnimationFrame(() => {
      try {
        JsBarcode(previewRef.current, product.sku || "N/A", {
          format: "CODE128",
          width: 1.6,
          height: SIZES[size].barcodeH,
          fontSize: SIZES[size].fontSize,
          margin: 2,
          displayValue: true,
        });
      } catch (e) {
        console.warn("Label preview barcode failed:", e);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [open, product, size]);

  const doPrint = () => {
    if (!product) return;
    const s = SIZES[size];
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    const barcodeSvgs = Array.from({ length: Number(copies) }).map((_, i) => `<svg id="bc-${i}"></svg>`).join("");
    w.document.write(`<html><head><title>Labels — ${product.name}</title>
      <style>
        @media print { @page { size: A4; margin: 6mm; } }
        body { font-family: 'Inter', 'Arial', sans-serif; margin: 0; padding: 0; }
        .grid { display: grid; grid-template-columns: repeat(auto-fill, ${s.w}); gap: 2mm; padding: 4mm; }
        .label { width: ${s.w}; height: ${s.h}; border: 1px dashed #999; padding: 1mm; box-sizing: border-box; text-align: center; display: flex; flex-direction: column; justify-content: space-between; align-items: center; overflow: hidden; }
        .brand { font-size: ${s.fontSize}px; font-weight: 700; letter-spacing: 0.3px; }
        .name { font-size: ${s.fontSize}px; font-weight: 600; line-height: 1.15; margin: 1px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; width: 100%; }
        .meta { font-size: ${s.fontSize - 1}px; color: #333; }
        .price { font-size: ${s.fontSize + 2}px; font-weight: 700; }
        svg { max-width: 100%; height: ${s.barcodeH}px; }
      </style>
    </head><body>
      <div class="grid">
        ${Array.from({ length: Number(copies) }).map((_, i) => `
          <div class="label">
            <div class="brand">BAKEMILL</div>
            <div class="name">${escape(product.name || "")}</div>
            <svg id="bc-${i}"></svg>
            ${showBatch && batch ? `<div class="meta">Batch: ${escape(batch)}</div>` : ""}
            ${showPrice ? `<div class="price">₹${Math.round(Number(product.sale_price || 0))}</div>` : ""}
          </div>
        `).join("")}
      </div>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11/dist/JsBarcode.all.min.js"></script>
      <script>
        window.addEventListener('load', () => {
          for (let i = 0; i < ${Number(copies)}; i++) {
            try { JsBarcode('#bc-' + i, '${escape(product.sku || "N/A")}', { format:'CODE128', width:1.6, height:${s.barcodeH}, fontSize:${s.fontSize}, margin:1, displayValue:true }); } catch(e){}
          }
          setTimeout(() => window.print(), 400);
        });
      </script>
    </body></html>`);
    w.document.close();
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <BarcodeIcon className="w-5 h-5 text-[#C05621]" /> Print barcode labels
          </DialogTitle>
          <DialogDescription>Print N × M sheet of labels with the product's SKU as a CODE128 barcode. Compatible with any standard USB / Bluetooth handheld scanner.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 py-2">
          {/* Options */}
          <div className="space-y-3">
            <div>
              <Label>Product</Label>
              <div className="mt-1 p-3 border border-[#EAE5D9] rounded-md bg-[#FDFBF7]">
                <div className="font-medium">{product.name}</div>
                <div className="text-xs text-[#8B7355] font-mono">SKU: {product.sku}</div>
              </div>
            </div>

            <div>
              <Label>Production batch (optional)</Label>
              <Select value={batch || "none"} onValueChange={(v) => setBatch(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="label-batch-select"><SelectValue placeholder="No batch — SKU only" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">No batch — SKU only</SelectItem>
                  {batches.map((b) => (
                    <SelectItem key={b.id} value={b.batch_no}>
                      {b.batch_no} · {b.quantity} units · {new Date(b.produced_at).toLocaleDateString("en-IN")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {batches.length === 0 && (
                <div className="text-xs text-[#8B7355] mt-1">No production batches yet. Run one from Manufacturing to attach a batch to labels.</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Copies</Label>
                <Input type="number" min="1" max="200" value={copies} onChange={(e) => setCopies(Math.max(1, Math.min(200, Number(e.target.value) || 1)))} data-testid="label-copies" />
              </div>
              <div>
                <Label>Label size</Label>
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger data-testid="label-size"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(SIZES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-sm pt-2">
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={showPrice} onChange={(e) => setShowPrice(e.target.checked)} /> Show price
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={showBatch} onChange={(e) => setShowBatch(e.target.checked)} /> Show batch #
              </label>
            </div>
          </div>

          {/* Preview */}
          <div>
            <Label>Preview</Label>
            <div className="mt-1 rounded-md border border-[#EAE5D9] bg-[#FDFBF7] p-4 flex items-center justify-center">
              <div style={{ width: SIZES[size].w, height: SIZES[size].h, border: "1px dashed #999", padding: "2mm", boxSizing: "border-box", background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", overflow: "hidden" }}>
                <div style={{ fontSize: SIZES[size].fontSize, fontWeight: 700, letterSpacing: 0.3 }}>BAKEMILL</div>
                <div style={{ fontSize: SIZES[size].fontSize, fontWeight: 600, textAlign: "center", lineHeight: 1.15, maxWidth: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{product.name}</div>
                <svg ref={previewRef} data-testid="label-barcode-preview" />
                {showBatch && batch && <div style={{ fontSize: SIZES[size].fontSize - 1, color: "#333" }}>Batch: {batch}</div>}
                {showPrice && <div style={{ fontSize: SIZES[size].fontSize + 2, fontWeight: 700 }}>₹{Math.round(Number(product.sale_price || 0))}</div>}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={doPrint} data-testid="label-print-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
            <Printer className="w-4 h-4 mr-1" /> Print {copies} labels
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function escape(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
